import { useEffect, useState } from 'react'

import { Box, Typography } from '@mui/material'

import type { AuthenticatorVerifyResultSchema } from '@/api/types'

import { api } from '@/api'
import { VerifyCodeForm } from '@/components/auth/verify-code-form'
import { Button } from '@/components/ui/button'
import { Stepper } from '@/components/ui/stepper'
import { getErrorMessage, isAccountLockedError } from '@/utils/error'

import { AuthenticatorQrPanel } from './authenticator-qr-panel'
import { RecoveryCodesList } from './recovery-codes-list'

interface Props {
  onEnrolled: (result: AuthenticatorVerifyResultSchema) => void
  onAccountLocked?: VoidFunction
}

type Step = 'scan' | 'verify' | 'codes'

const STEP_INDEX: Record<Step, number> = { scan: 0, verify: 1, codes: 2 }

export function AuthenticatorEnrollment({ onEnrolled, onAccountLocked }: Props) {
  const [step, setStep] = useState<Step>('scan')
  const [result, setResult] = useState<AuthenticatorVerifyResultSchema>()

  // Both calls answer with {Code, Description}; the message belongs next to the code field, not in a toast.
  const setup = api.useSetUpTwoFactorAuthenticator({ axios: { skipErrorToast: true } })

  const verify = api.useVerifyTwoFactorAuthenticator({
    mutation: {
      onSuccess: (response) => {
        setResult(response.data)
        setStep('codes')
      },
      onError: (error) => {
        if (isAccountLockedError(error)) onAccountLocked?.()
      },
    },
    axios: { skipErrorToast: true },
  })

  useEffect(() => {
    setup.mutate()
    // One key per enrolment; re-running would invalidate the code the user is typing.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setupInfo = setup.data?.data
  const sharedKey = setupInfo?.sharedKey
  const authenticatorUri = setupInfo?.authenticatorUri
  const setupError = setup.error ? getErrorMessage(setup.error) : undefined

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stepper activeStep={STEP_INDEX[step]} steps={3} />
      {step === 'scan' && (
        <>
          {!!setupError && (
            <Typography variant="body2" color="error">
              {setupError}
            </Typography>
          )}
          {!!sharedKey && !!authenticatorUri && (
            <>
              <AuthenticatorQrPanel sharedKey={sharedKey} authenticatorUri={authenticatorUri} />
              <Button color="primary" variant="contained" size="large" fullWidth onClick={() => setStep('verify')}>
                Next
              </Button>
            </>
          )}
        </>
      )}
      {step === 'verify' && (
        <VerifyCodeForm
          variant="authenticator"
          isPending={verify.isPending}
          errorMessage={verify.error ? getErrorMessage(verify.error) : undefined}
          onVerify={(code) => verify.mutate({ data: { code } })}
          onBack={() => setStep('scan')}
        />
      )}
      {step === 'codes' && !!result && (
        <RecoveryCodesList codes={result.recoveryCodes ?? []} onContinue={() => onEnrolled(result)} />
      )}
    </Box>
  )
}
